"use client";

import { c as _c } from "react/compiler-runtime";
import { IconArrowRightOutline18, IconArrowUpRightOutline18, IconBoltOutline18, IconCircleInfoOutline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";
import { EnhancedJsonViewWithCopy, hasNestedContent, type ExecutionContext } from "@/components/ui/enhanced-json-view";
import { Button } from "@/components/ui/button";
import { useActions, useLogicActions, useTriggers } from "@/hooks/useActionTriggerDefinitions";
import type { FixWithAIErrorContext } from "@/hooks/useFixWithAI";
import { useOrganizations } from "@/hooks/useOrganizations";
import { getChainInfo, getNativeTokenDecimals } from "@/lib/chains";
import { isNativeTokenAddress } from "@/lib/tokens/nativeToken";
import { openSupport } from "@/lib/support";
import { getSyntaxHighlightingStyle } from "@/lib/syntax-highlighting-style";
import { toast } from "@/lib/toast";
import { capitalizeWords, getExplorerTxUrl } from "@/lib/utils";
import { resolveNodeDefinition, resolveNodeVisual } from "@/lib/workflow/nodeVisual";
import { cancelWorkflowRun, retryWorkflowRun, startWorkflowRerun } from "@/lib/workflowRunActions";
import { useSlackChannels } from "@/app/(v2)/workflows/_hooks/useSlackChannels";
import type { SlackChannel } from "@/app/(v2)/workflows/_components/form-fields/slackChannels";
import { resolveSlackConversationLabel } from "@/app/(v2)/workflows/_components/form-fields/slackChannels";
import { useConnectorsStore } from "@/stores/connectorsStore";
import { useWalletsStore } from "@/stores/walletsStore";
import type { BlockExpansionMeta, WorkflowNodePayload, WorkflowRun } from "@/stores/workflowRunsStore";
import { useWorkflowRunsStore } from "@/stores/workflowRunsStore";
import { useWorkflow } from "@/app/(v2)/workflows/_hooks/useWorkflow";
import { useWorkflowsStore } from "@/stores/workflowsStore";
import { useColorMode } from "@chakra-ui/react";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { formatUnits } from "viem";
import { FundsMovementBanner } from "./FundsMovementBanner";
import { IterationStepRow } from "./IterationStepRow";
import { ResultDisplay } from "./ResultDisplay";
import { RunFundsSummary } from "./RunFundsSummary";
import { RunTimelineHeader, RunTimelineNode } from "./RunTimeline";

// ---------------------------------------------------------------------------
// RunDetailSkeleton — mirrors the final RunDetailContent shape so loading
// doesn't cause layout shift when run data arrives (header + timeline rows).
// ---------------------------------------------------------------------------

function RunDetailSkeleton(t0) {
  const $ = _c(10);
  const {
    compact: t1,
    rows: t2
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const rows = t2 === undefined ? 5 : t2;
  let t3;
  if ($[0] !== compact) {
    t3 = compact ? <div className="border-ds-border-default mb-4 flex flex-wrap items-center gap-2 border-b px-1 pb-4"><div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 h-6 w-20 rounded-full" /><div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 h-3 w-16 rounded" /><div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 h-3 w-12 rounded" /><div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 h-3 w-20 rounded" /><div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 h-3 w-28 rounded" /><div className="ml-auto flex gap-1"><div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 h-5 w-5 rounded" /><div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 h-5 w-5 rounded" /></div></div> : <div className="mb-6 space-y-3"><div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 h-7 w-40 rounded" /><div className="flex flex-wrap items-center gap-2"><div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 h-6 w-20 rounded-full" /><div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 h-3 w-16 rounded" /><div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 h-3 w-20 rounded" /><div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 h-3 w-28 rounded" /><div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 h-3 w-24 rounded" /></div></div>;
    $[0] = compact;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== rows) {
    t4 = Array.from({
      length: rows
    });
    $[2] = rows;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== rows || $[5] !== t4) {
    t5 = <div>{t4.map((_, index) => {
        const isLast = index === rows - 1;
        return <div key={index}><div className="relative flex items-center gap-3 rounded-lg">{index > 0 && <div className="border-ds-neutral-300 absolute bottom-1/2 left-[39px] top-0 w-0 border-l-2 border-dashed dark:border-zinc-600" />}{!isLast && <div className="border-ds-neutral-300 absolute bottom-0 left-[39px] top-1/2 w-0 border-l-2 border-dashed dark:border-zinc-600" />}<div className="h-3.5 w-3.5 flex-shrink-0" /><div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 relative z-10 h-7 w-7 flex-shrink-0 rounded-full" /><div className="flex min-w-0 flex-1 items-center gap-3 py-1.5"><div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 h-12 w-12 flex-shrink-0 rounded-xl" /><div className="flex min-w-0 flex-1 flex-col gap-1.5"><div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 h-4 rounded" style={{
                  width: `${50 + index * 13 % 35}%`
                }} /><div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 h-2.5 rounded" style={{
                  width: `${25 + index * 7 % 20}%`
                }} /></div></div></div><div className="flex gap-3"><div className="w-3.5 flex-shrink-0" /><div className="flex w-7 flex-shrink-0 flex-col items-center">{!isLast && <div className="border-ds-neutral-300 w-px flex-1 border-l-2 border-dashed dark:border-zinc-600" />}</div><div className={!isLast ? "pb-5" : "pb-1"} /></div></div>;
      })}</div>;
    $[4] = rows;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t5) {
    t6 = <div className="animate-pulse">{t3}{t5}</div>;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}

// ---------------------------------------------------------------------------
// AutoSwapBanner
// ---------------------------------------------------------------------------

interface AutoSwapEvent {
  sourceWalletId: string;
  actionWalletAddress?: string;
  srcChainId: number;
  srcToken: string;
  srcTokenSymbol?: string;
  dstChainId: number;
  dstToken: string;
  dstTokenSymbol?: string;
  amountOut: string;
  dstTokenDecimals: number;
  swapResult?: Record<string, unknown>;
}
function formatSwapAmount(amountOut: string, decimals: number): string {
  try {
    const val = formatUnits(BigInt(amountOut), decimals);
    const num = parseFloat(val);
    if (num === 0) return "0";
    if (num < 0.000001) return "<0.000001";
    return num.toPrecision(6).replace(/\.?0+$/, "");
  } catch {
    return amountOut;
  }
}
function chainName(chainId: number): string {
  return getChainInfo(chainId).name;
}
function tokenLabel(token: string, chainId: number): string {
  if (!token || isNativeTokenAddress(token)) return getChainInfo(chainId).gasToken;
  return token.slice(0, 6) + "…";
}
function walletLabel(wallet: {
  name: string;
  address: string;
} | undefined | null): string | null {
  if (!wallet) return null;
  return wallet.name || `${wallet.address.slice(0, 6)}…${wallet.address.slice(-4)}`;
}
function truncateAddress(address: string): string {
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}
function AutoSwapBanner(t0) {
  const $ = _c(25);
  const {
    events,
    getWalletById,
    getWalletByAddress
  } = t0;
  if (events.length === 0) {
    return null;
  }
  const dst = events[0];
  let t1;
  if ($[0] !== dst.dstChainId || $[1] !== dst.dstToken || $[2] !== dst.dstTokenSymbol) {
    t1 = dst.dstTokenSymbol || tokenLabel(dst.dstToken, dst.dstChainId);
    $[0] = dst.dstChainId;
    $[1] = dst.dstToken;
    $[2] = dst.dstTokenSymbol;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const dstSymbol = t1;
  let t2;
  if ($[4] !== dst.dstChainId) {
    t2 = chainName(dst.dstChainId);
    $[4] = dst.dstChainId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const dstChain = t2;
  const isMulti = events.length > 1;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <IconBoltOutline18 className="h-3.5 w-3.5 flex-shrink-0 text-amber-500" aria-hidden="true" />;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = isMulti ? ` · ${events.length} sources` : "";
  let t5;
  if ($[7] !== t4) {
    t5 = <div className="flex items-center gap-2">{t3}<p className="label text-amber-700 dark:text-amber-400">Automatically funded{t4}</p></div>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== dstChain || $[10] !== dstSymbol) {
    t6 = <p className="mt-1 text-xs text-amber-600 dark:text-amber-500">Swapped tokens to cover the required {dstSymbol} on {dstChain}</p>;
    $[9] = dstChain;
    $[10] = dstSymbol;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== events || $[13] !== getWalletByAddress || $[14] !== getWalletById) {
    let t8;
    if ($[16] !== getWalletByAddress || $[17] !== getWalletById) {
      t8 = (event, i) => {
        const srcSymbol = event.srcTokenSymbol || tokenLabel(event.srcToken, event.srcChainId);
        const srcChain = chainName(event.srcChainId);
        const dstChain_0 = chainName(event.dstChainId);
        const rowDstSymbol = event.dstTokenSymbol || tokenLabel(event.dstToken, event.dstChainId);
        const decimals = event.dstTokenDecimals ?? getNativeTokenDecimals(event.dstChainId);
        const amount = formatSwapAmount(event.amountOut, decimals);
        const srcWalletLabel = walletLabel(getWalletById?.(event.sourceWalletId));
        const dstWallet = event.actionWalletAddress ? getWalletByAddress?.(event.actionWalletAddress) : undefined;
        const dstWalletLabel = walletLabel(dstWallet) ?? (event.actionWalletAddress ? truncateAddress(event.actionWalletAddress) : null);
        return <div key={i} className="flex flex-wrap items-center gap-1.5 text-xs text-amber-600 dark:text-amber-500"><span>{srcWalletLabel && <span className="opacity-60">{srcWalletLabel} </span>}{srcSymbol} on {srcChain}</span><IconArrowRightOutline18 className="h-3 w-3 flex-shrink-0 opacity-50" aria-hidden="true" /><span className="font-medium">{dstWalletLabel && <span className="font-normal opacity-60">{dstWalletLabel} </span>}{amount} {rowDstSymbol} on {dstChain_0}</span>{!!event.swapResult?.srcTransactionHash && <><span className="opacity-40">·</span><a href={getExplorerTxUrl(event.srcChainId, event.swapResult.srcTransactionHash as string)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 underline underline-offset-2 hover:text-amber-400">Tx <IconArrowUpRightOutline18 className="h-2.5 w-2.5" aria-hidden="true" /></a>{!!event.swapResult.dstTransactionHash && event.swapResult.dstTransactionHash !== event.swapResult.srcTransactionHash && <a href={getExplorerTxUrl(event.dstChainId, event.swapResult.dstTransactionHash as string)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 underline underline-offset-2 hover:text-amber-400">Dest tx <IconArrowUpRightOutline18 className="h-2.5 w-2.5" /></a>}</>}</div>;
      };
      $[16] = getWalletByAddress;
      $[17] = getWalletById;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    t7 = events.map(t8);
    $[12] = events;
    $[13] = getWalletByAddress;
    $[14] = getWalletById;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[19] !== t7) {
    t8 = <div className="mt-2 space-y-1.5 border-t border-amber-200/60 pt-2 dark:border-amber-700/30">{t7}</div>;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t5 || $[22] !== t6 || $[23] !== t8) {
    t9 = <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 dark:border-amber-800/50 dark:bg-amber-900/20">{t5}{t6}{t8}</div>;
    $[21] = t5;
    $[22] = t6;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}
function DustOverridesBanner(t0) {
  const $ = _c(14);
  const {
    raw
  } = t0;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== raw) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const entries = Object.entries(raw as Record<string, unknown>).filter(_temp) as [string, {
        original: unknown;
        dust: unknown;
        tokenPrice?: unknown;
      }][];
      if (entries.length === 0) {
        t5 = null;
        break bb0;
      }
      t3 = "rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 dark:border-amber-800/50 dark:bg-amber-900/20";
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <div className="flex items-center gap-2"><IconCircleInfoOutline18 className="h-3.5 w-3.5 flex-shrink-0 text-amber-500" aria-hidden="true" /><p className="label text-amber-700 dark:text-amber-400">Dust overrides applied</p></div>;
        $[6] = t4;
      } else {
        t4 = $[6];
      }
      t1 = "mt-1.5 space-y-0.5";
      t2 = entries.map(_temp2);
    }
    $[0] = raw;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[7] !== t1 || $[8] !== t2) {
    t6 = <div className={t1}>{t2}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t6) {
    t7 = <div className={t3}>{t4}{t6}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
function _temp2(t0) {
  const [field, info] = t0;
  return <p key={field} className="text-xs text-amber-600 dark:text-amber-500"><span className="font-medium">{field}:</span> {String(info.original)}{" "}<IconArrowRightOutline18 className="inline h-3 w-3" /> {String(info.dust)}{typeof info.tokenPrice === "number" && <span className="ml-1 text-amber-500 dark:text-amber-600">(price: ${info.tokenPrice < 0.01 ? info.tokenPrice.toPrecision(3) : info.tokenPrice.toFixed(2)})</span>}</p>;
}
function _temp(t0) {
  const [, v] = t0;
  return v && typeof v === "object";
}
function getWorkflowTraversalChildren(node?: WorkflowNodePayload): string[] {
  if (!node) return [];
  if (node.type === "for-each") {
    return [...(node.loopBody ?? []), ...(node.children ?? [])];
  }
  return [...(node.children ?? []), ...(node.loopBody ?? [])];
}
function collectDefinitionSubtreeOrder(workflowNodes: Record<string, WorkflowNodePayload>, rootIds: string[]): string[] {
  const ordered: string[] = [];
  const visited = new Set<string>();
  const visit = (nodeId: string) => {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    ordered.push(nodeId);
    for (const childId of getWorkflowTraversalChildren(workflowNodes[nodeId])) {
      visit(childId);
    }
  };
  for (const rootId of rootIds) {
    visit(rootId);
  }
  return ordered;
}
function collectForEachLoopBodyDescendantIds(workflowNodes: Record<string, WorkflowNodePayload>): Set<string> {
  const nestedIds = new Set<string>();
  for (const [nodeId, nodeData] of Object.entries(workflowNodes)) {
    if (nodeData.type !== "for-each") continue;
    for (const nestedId of collectDefinitionSubtreeOrder(workflowNodes, nodeData.loopBody ?? [])) {
      nestedIds.add(nestedId);
    }
  }
  return nestedIds;
}
function getIterationEntriesInDefinitionOrder(workflowNodes: Record<string, WorkflowNodePayload>, forEachNodeId: string, iteration: Record<string, any>): Array<[string, any]> {
  const orderedIds = collectDefinitionSubtreeOrder(workflowNodes, workflowNodes[forEachNodeId]?.loopBody ?? []);
  const iterationIds = new Set(Object.keys(iteration));
  const consumedIds = new Set<string>();
  const orderedEntries: Array<[string, any]> = [];
  for (const nodeId of orderedIds) {
    if (!iterationIds.has(nodeId)) continue;
    consumedIds.add(nodeId);
    orderedEntries.push([nodeId, iteration[nodeId]]);
  }
  for (const [nodeId, nodeState] of Object.entries(iteration)) {
    if (consumedIds.has(nodeId)) continue;
    orderedEntries.push([nodeId, nodeState]);
  }
  return orderedEntries;
}
const SLACK_SEND_NODE_TYPES = new Set(["send-slack-message", "slack-send-message", "slack-send-block-kit-message", "slack-send-message-to-user-or-group"]);
function resolveSlackTargetLabel(target: string, slackChannelsMap?: Map<string, SlackChannel>): string | null {
  const trimmed = target.trim();
  if (!trimmed) return null;
  const resolved = resolveSlackConversationLabel(trimmed, slackChannelsMap);
  if (!resolved) return trimmed;

  // Preserve raw IDs if lookup data isn't available for this workspace.
  if ((resolved === "Slack channel" || resolved === "Slack user") && resolved !== trimmed) {
    return trimmed;
  }
  return resolved;
}
function readSlackResultChannel(result?: Record<string, unknown>): string | null {
  const channel = (result?.data as {
    ret?: {
      channel?: unknown;
    };
  } | undefined)?.ret?.channel;
  return typeof channel === "string" && channel.trim() ? channel.trim() : null;
}
function readSlackUsersLabel(usersValue: unknown, slackChannelsMap?: Map<string, SlackChannel>): string | null {
  const rawUsers = Array.isArray(usersValue) ? usersValue.filter((value): value is string => typeof value === "string") : typeof usersValue === "string" ? usersValue.split(",") : [];
  const labels = rawUsers.map(user => resolveSlackTargetLabel(user, slackChannelsMap) || user.trim()).filter(Boolean);
  return labels.length > 0 ? labels.join(", ") : null;
}
function getSlackPayloadHeading(nodeType: string, payload?: Record<string, unknown>, result?: Record<string, unknown>, slackChannelsMap?: Map<string, SlackChannel>): string | null {
  if (!SLACK_SEND_NODE_TYPES.has(nodeType)) return null;
  if (nodeType === "slack-send-message-to-user-or-group") {
    const usersLabel = readSlackUsersLabel(payload?.users, slackChannelsMap);
    if (usersLabel) return usersLabel;
  }
  const payloadTarget = typeof payload?.channel === "string" ? payload.channel : typeof payload?.conversation === "string" ? payload.conversation : null;
  const resultChannel = readSlackResultChannel(result);
  const target = payloadTarget || resultChannel;
  if (!target) return null;
  return resolveSlackTargetLabel(target, slackChannelsMap) || target;
}
function PayloadSectionHeader(t0) {
  const $ = _c(4);
  const {
    destination
  } = t0;
  if (!destination) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <label className="micro-label">Payload</label>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="micro-label shrink-0">Sent to</span>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== destination) {
    t2 = <div className="flex min-w-0 items-center gap-2">{t1}<span className="border-ds-border-default text-ds-text-secondary dark:bg-ds-neutral-900/70 text-2xs max-w-full truncate rounded-full border bg-zinc-50 px-2 py-0.5 font-medium dark:text-zinc-200" title={destination}>{destination}</span></div>;
    $[2] = destination;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}

/**
 * CodeTransformPayload - Special payload renderer for code-transform nodes.
 * Extracts the `code` field and renders it with syntax highlighting,
 * then shows remaining payload fields in the normal JSON viewer.
 */
export function CodeTransformPayload(t0) {
  const $ = _c(19);
  const {
    payload,
    expandAll: t1,
    executionState
  } = t0;
  const expandAll = t1 === undefined ? false : t1;
  const {
    colorMode
  } = useColorMode();
  const isDark = colorMode === "dark";
  let t2;
  let t3;
  if ($[0] !== isDark) {
    t3 = getSyntaxHighlightingStyle(isDark);
    $[0] = isDark;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  t2 = t3;
  const syntaxStyle = t2;
  const code = typeof payload.code === "string" ? payload.code : null;
  let t4;
  bb0: {
    if (!code) {
      t4 = payload;
      break bb0;
    }
    let rest;
    if ($[2] !== payload) {
      const {
        code: _,
        ...t5
      } = payload;
      rest = t5;
      $[2] = payload;
      $[3] = rest;
    } else {
      rest = $[3];
    }
    let parsed;
    if ($[4] !== rest) {
      parsed = {};
      for (const [key, val] of Object.entries(rest)) {
        if (typeof val === "string" && val.startsWith("{")) {
          try {
            parsed[key] = JSON.parse(val);
          } catch {
            parsed[key] = val;
          }
        } else {
          parsed[key] = val;
        }
      }
      $[4] = rest;
      $[5] = parsed;
    } else {
      parsed = $[5];
    }
    t4 = parsed;
  }
  const restPayload = t4;
  const hasRest = Object.keys(restPayload).length > 0;
  let t5;
  if ($[6] !== code || $[7] !== isDark || $[8] !== syntaxStyle) {
    t5 = code && <div><label className="micro-label">Code</label><div className="emboss-inner !bg-ds-neutral-100 dark:!bg-ds-neutral-800 mt-1 max-h-[50dvh] overflow-y-auto rounded-lg"><SyntaxHighlighter language="javascript" style={syntaxStyle} customStyle={{
          margin: 0,
          padding: "1rem",
          background: "transparent",
          fontSize: "0.75rem",
          lineHeight: "1.5"
        }} showLineNumbers={true} lineNumberStyle={{
          minWidth: "2em",
          paddingRight: "1em",
          color: isDark ? "#585b70" : "#b4b4b4",
          userSelect: "none"
        }}>{code}</SyntaxHighlighter></div></div>;
    $[6] = code;
    $[7] = isDark;
    $[8] = syntaxStyle;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== code || $[11] !== executionState || $[12] !== expandAll || $[13] !== hasRest || $[14] !== restPayload) {
    t6 = hasRest && <div>{code && <label className="micro-label">Other Fields</label>}<div className="emboss-inner !bg-ds-neutral-100 dark:!bg-ds-neutral-800 mt-1 max-h-[50dvh] overflow-y-auto rounded-lg p-4 font-mono text-xs"><EnhancedJsonViewWithCopy value={restPayload} defaultExpanded={true} expandAll={expandAll} executionState={executionState} showExpandAll={false} /></div></div>;
    $[10] = code;
    $[11] = executionState;
    $[12] = expandAll;
    $[13] = hasRest;
    $[14] = restPayload;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t5 || $[17] !== t6) {
    t7 = <div className="space-y-3">{t5}{t6}</div>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}
interface RunDetailContentProps {
  runId: string;
  /** If true, hides the navigation links (back to history, view workflow) */
  compact?: boolean;
  /** Called when close/back button is clicked in compact mode */
  onClose?: () => void;
  /** Hide the top run header and only render the timeline/details */
  hideHeader?: boolean;
  /** Preloaded run data; when provided, skips authenticated run fetches. */
  runData?: WorkflowRun | null;
  /** Optional workflow name override when the workflow store is unavailable. */
  workflowNameOverride?: string;
  /** Optional workflow destination used by the shared header link. */
  workflowHref?: string | null;
  /** Whether rerun/cancel actions should be exposed in the shared header. */
  allowRunActions?: boolean;
  /** Optional editor-aware Fix with Caddie callback for inline error panels. */
  onFixWithAI?: (errorCtx?: FixWithAIErrorContext) => void;
  /** Whether to show the top-level consolidated funds summary banner. */
  showFundsSummary?: boolean;
}
export default function RunDetailContent({
  runId,
  compact = false,
  onClose,
  hideHeader = false,
  runData,
  workflowNameOverride,
  workflowHref,
  allowRunActions = true,
  onFixWithAI,
  showFundsSummary = true
}: RunDetailContentProps) {
  const router = useRouter();
  const [run, setRun] = useState<WorkflowRun | null>(runData ?? null);
  const [loading, setLoading] = useState(runData === undefined);
  const [error, setError] = useState<Error | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [expandedPayloads, setExpandedPayloads] = useState<Set<string>>(new Set());
  const [cancelling, setCancelling] = useState(false);
  const [expandedIterationSteps, setExpandedIterationSteps] = useState<Set<string>>(new Set());

  // Fetch trigger/action definitions for badge display
  const {
    triggers
  } = useTriggers();
  const {
    actions
  } = useActions();
  const {
    logicActions
  } = useLogicActions();
  const connectorTypes = useConnectorsStore(state => state.connectorTypes);
  const connectors = useConnectorsStore(state_0 => state_0.connectors);
  const loadConnectors = useConnectorsStore(state_1 => state_1.loadConnectors);
  const wallets = useWalletsStore(state_2 => state_2.wallets);
  const loadWallets = useWalletsStore(state_3 => state_3.loadWallets);
  const getWalletById = useWalletsStore(state_4 => state_4.getWalletById);
  const getWalletByAddress = useWalletsStore(state_5 => state_5.getWalletByAddress);
  const allDefinitions = useMemo(() => [...triggers, ...actions, ...logicActions], [triggers, actions, logicActions]);
  const definitionMap = useMemo(() => {
    return new Map(allDefinitions.map(def => [def.type, def]));
  }, [allDefinitions]);

  // Using single selectors following DRY principle
  const getRunById = useWorkflowRunsStore(state_6 => state_6.getRunById);
  const getDuration = useWorkflowRunsStore(state_7 => state_7.getDuration);
  const runWorkflow = useWorkflowsStore(state_8 => state_8.runWorkflow);
  const cancelRun = useWorkflowRunsStore(state_9 => state_9.cancelRun);
  const retryRun = useWorkflowRunsStore(state_10 => state_10.retryRun);

  // Fetch ONLY the workflow this run belongs to (previously preloaded full list).
  // Enabled once we know the workflowId; cache is shared with list detail reads.
  const workflowQuery = useWorkflow(run?.workflowId, {
    enabled: !!run?.workflowId
  });
  const handleGetSupport = useCallback(() => {
    const wfName = workflowNameOverride || workflowQuery.data?.name;
    const workflowLabel = wfName?.trim() ? `"${wfName}"` : "my workflow";
    const subject = `Help with run on ${workflowLabel}`;
    const text = runId ? `Run ID: ${runId}` : undefined;
    openSupport({
      subject,
      text
    });
  }, [runId, workflowNameOverride, workflowQuery.data?.name]);
  const handleRerun = useCallback(async () => {
    if (!run) return;
    const {
      triggeredAt: _,
      ...inputFields
    } = run.executionState?.root?.result ?? {};
    const payload = Object.keys(inputFields).length > 0 ? inputFields : undefined;
    const newRunId = await startWorkflowRerun({
      workflowId: run.workflowId,
      runWorkflow,
      payload: payload ? {
        payload
      } : undefined
    });
    router.push(`/workflows/history/${newRunId}`);
  }, [run, runWorkflow, router]);
  const handleRetry = useCallback(async () => {
    if (!run) return;
    const newRunId_0 = await retryWorkflowRun({
      workflowId: run.workflowId,
      runId: run.id,
      retryRun
    });
    router.push(`/workflows/history/${newRunId_0}`);
  }, [run, retryRun, router]);
  const handleCancel = useCallback(async () => {
    if (!run || cancelling) return;
    setCancelling(true);
    try {
      await cancelWorkflowRun({
        workflowId: run.workflowId,
        runId: run.id,
        cancelRun
      });
      setRun(prev => prev ? {
        ...prev,
        status: "cancelled" as const
      } : prev);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to cancel run");
    } finally {
      setCancelling(false);
    }
  }, [run, cancelling, cancelRun]);
  const handleCopyRunJson = useCallback(() => {
    if (!run) return;
    const runJson = JSON.stringify({
      id: run.id,
      workflowId: run.workflowId,
      status: run.status,
      startedAt: run.startedAt,
      finishedAt: run.finishedAt,
      workflowDefinition: run.workflowDefinition,
      executionState: run.executionState
    }, null, 2);
    copyToClipboard(runJson, "runJson");
  }, [run]);
  const handleCopyDefinition = useCallback(() => {
    if (!run?.workflowDefinition) return;
    const definitionJson = JSON.stringify(run.workflowDefinition, null, 2);
    copyToClipboard(definitionJson, "definition");
  }, [run]);

  // Helper to get node type badge (trigger, action, or logic)
  const getNodeTypeBadge = (nodeType: string) => {
    const isTrigger = triggers.some((t: {
      type: string;
    }) => t.type === nodeType);
    const isAction = actions.some((a: {
      type: string;
    }) => a.type === nodeType);
    const isLogic = logicActions.some((l: {
      type: string;
    }) => l.type === nodeType);
    if (isTrigger) {
      return {
        label: "Trigger",
        className: "bg-yellow-50 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-400"
      };
    }
    if (isAction) {
      return {
        label: "Action",
        className: "bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400"
      };
    }
    if (isLogic) {
      return {
        label: "Logic",
        className: "bg-purple-50 text-purple-700 dark:bg-purple-500/10 dark:text-purple-400"
      };
    }
    return null;
  };

  // Helper to get node display name from definitions
  const getNodeDisplayName = (nodeType_0: string) => {
    const definition = resolveNodeDefinition(nodeType_0, allDefinitions);
    if (definition?.name) return definition.name;
    return capitalizeWords(nodeType_0);
  };
  const {
    selectedOrgId
  } = useOrganizations();

  // Sync run state when runData prop changes (adjust state during render)
  const [prevRunData, setPrevRunData] = useState(runData);
  if (runData !== prevRunData) {
    setPrevRunData(runData);
    if (runData !== undefined) {
      setRun(runData);
      setError(runData ? null : new Error("Run not found"));
      setLoading(false);
    }
  }

  // Load run details and workflows on mount
  useEffect(() => {
    if (runData !== undefined) return;
    const loadRunDetails = async () => {
      try {
        setLoading(true);
        const runData_0 = await getRunById(runId);
        setRun(runData_0);
        setError(null);
      } catch (e_0) {
        const err = e_0 instanceof Error ? e_0 : new Error("Failed to load run details");
        console.error("Failed to load run details", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    loadRunDetails();
    if (selectedOrgId) {
      loadConnectors();
      loadWallets();
    }
  }, [runData, runId, getRunById, loadConnectors, loadWallets, selectedOrgId]);

  // Set default expanded nodes based on run status
  useEffect(() => {
    if (!run || !run.workflowDefinition || !run.executionState) return;
    const workflowDef = run.workflowDefinition;
    const nodes = Object.entries(workflowDef.nodes);
    const executionState = run.executionState;

    // Find nodes inside for-each loop bodies to exclude from top-level.
    const insideForEach = collectForEachLoopBodyDescendantIds(workflowDef.nodes);

    // Also exclude nodes inside block expansions
    const insideBlocks = new Set<string>();
    if (workflowDef.blockExpansions) {
      for (const meta of Object.values(workflowDef.blockExpansions)) {
        for (const id of meta.expandedIds) {
          insideBlocks.add(id);
        }
      }
    }
    const topLevelNodes = nodes.filter(([nodeId]) => !insideForEach.has(nodeId) && !insideBlocks.has(nodeId));

    // Auto-expand block groups that contain failures
    if (workflowDef.blockExpansions) {
      const autoExpandBlocks = new Set<string>();
      for (const [blockNodeId, meta_0] of Object.entries(workflowDef.blockExpansions)) {
        const hasFailure = meta_0.expandedIds.some(id_0 => executionState[id_0]?.status === "failure");
        if (hasFailure) {
          autoExpandBlocks.add(blockNodeId);
        }
      }
      if (autoExpandBlocks.size > 0) {
        setExpandedBlockGroups(autoExpandBlocks);
      }
    }

    // Sort by finishedAt time
    const sortedNodes = topLevelNodes.sort(([nodeIdA], [nodeIdB]) => {
      const nodeStateA = executionState[nodeIdA];
      const nodeStateB = executionState[nodeIdB];
      const finishedAtA = nodeStateA?.finishedAt ? new Date(nodeStateA.finishedAt).getTime() : 0;
      const finishedAtB = nodeStateB?.finishedAt ? new Date(nodeStateB.finishedAt).getTime() : 0;
      if (finishedAtA === 0 && finishedAtB === 0) return 0;
      if (finishedAtA === 0) return 1;
      if (finishedAtB === 0) return -1;
      return finishedAtA - finishedAtB;
    });

    // Simulation runs: collapse all nodes unless the run is degraded (auto-expand failure/partial nodes)
    if (run.isSimulation && run.status === "success") {
      setExpandedNodes(new Set());
      return;
    }

    // Find degraded nodes (failure or partial — partial covers for-each with failed iterations)
    // or the last successful node.
    const failedNodeIds = sortedNodes.filter(([nodeId_0]) => {
      const status = executionState[nodeId_0]?.status;
      return status === "failure" || status === "partial";
    }).map(([nodeId_1]) => nodeId_1);
    if (failedNodeIds.length > 0) {
      setExpandedNodes(new Set(failedNodeIds));
    } else {
      const successNodes = sortedNodes.filter(([nodeId_2]) => executionState[nodeId_2]?.status === "success");
      if (successNodes.length > 0) {
        const lastSuccessNodeId = successNodes[successNodes.length - 1][0];
        setExpandedNodes(new Set([lastSuccessNodeId]));
      } else if (sortedNodes.length > 0) {
        const lastNodeId = sortedNodes[sortedNodes.length - 1][0];
        setExpandedNodes(new Set([lastNodeId]));
      }
    }
  }, [run]);

  // Get workflow name
  const workflowName = useMemo(() => {
    if (workflowNameOverride) return workflowNameOverride;
    if (!run) return "";
    return workflowQuery.data?.name || run.workflowId;
  }, [workflowNameOverride, run, workflowQuery.data]);

  // Toggle node expansion
  const toggleNode = (nodeId_3: string) => {
    setExpandedNodes(prev_0 => {
      const next = new Set(prev_0);
      if (next.has(nodeId_3)) {
        next.delete(nodeId_3);
      } else {
        next.add(nodeId_3);
      }
      return next;
    });
  };

  // Copy to clipboard handler
  const copyToClipboard = async (text_0: string, id_1: string) => {
    try {
      await navigator.clipboard.writeText(text_0);
      setCopiedId(id_1);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err_0) {
      console.error("Failed to copy to clipboard", err_0);
    }
  };

  // Find all nodes that are inside a for-each loop
  const nodesInsideForEach = useMemo(() => {
    if (!run || !run.workflowDefinition) return new Set<string>();
    return collectForEachLoopBodyDescendantIds(run.workflowDefinition.nodes);
  }, [run]);

  // If a workflow has multiple Slack connectors from different workspaces,
  // only the first is used — IDs from other workspaces render as raw IDs.
  const workflowNodes = run?.workflowDefinition?.nodes;
  const slackConnectorInfo = useMemo(() => {
    if (!workflowNodes) return undefined;
    for (const nodeData of Object.values(workflowNodes)) {
      const conn = nodeData.connector;
      if (conn?.type?.includes("slack") && conn.id) {
        return {
          id: conn.id,
          type: conn.type
        };
      }
    }
    return undefined;
  }, [workflowNodes]);
  const {
    slackChannelsMap
  } = useSlackChannels(slackConnectorInfo?.id, slackConnectorInfo?.type, !!slackConnectorInfo);

  // Find all nodes that are inside block expansions and build block group metadata
  const {
    nodesInBlocks: nodesInBlocks_0,
    blockGroups: blockGroups_0
  } = useMemo(() => {
    const nodesInBlocks = new Set<string>();
    const blockGroups = new Map<string, {
      meta: BlockExpansionMeta;
      insertIndex: number;
    }>();
    if (!run?.workflowDefinition?.blockExpansions || !run.executionState) {
      return {
        nodesInBlocks,
        blockGroups
      };
    }
    const blockExpansions = run.workflowDefinition.blockExpansions;
    for (const [blockNodeId_0, meta_1] of Object.entries(blockExpansions)) {
      for (const expandedId of meta_1.expandedIds) {
        nodesInBlocks.add(expandedId);
      }

      // Determine insert position: use the earliest finishedAt among expanded nodes
      let earliestFinished = Infinity;
      for (const expandedId_0 of meta_1.expandedIds) {
        const state_11 = run.executionState[expandedId_0];
        if (state_11?.finishedAt) {
          const t_0 = new Date(state_11.finishedAt).getTime();
          if (t_0 < earliestFinished) earliestFinished = t_0;
        }
      }
      blockGroups.set(blockNodeId_0, {
        meta: meta_1,
        insertIndex: earliestFinished === Infinity ? 0 : earliestFinished
      });
    }
    return {
      nodesInBlocks,
      blockGroups
    };
  }, [run]);

  // Track expanded block groups in run timeline
  const [expandedBlockGroups, setExpandedBlockGroups] = useState<Set<string>>(new Set());
  const toggleBlockGroup = useCallback((blockNodeId_1: string) => {
    setExpandedBlockGroups(prev_1 => {
      const next_0 = new Set(prev_1);
      if (next_0.has(blockNodeId_1)) {
        next_0.delete(blockNodeId_1);
      } else {
        next_0.add(blockNodeId_1);
      }
      return next_0;
    });
  }, []);

  // Compute aggregate status for a block group
  const getBlockGroupStatus = useCallback((meta_2: BlockExpansionMeta): "success" | "partial" | "failure" | "running" | "waiting" | "pending" | "skipped" => {
    if (!run?.executionState) return "pending";
    const statuses = meta_2.expandedIds.map(id_2 => run.executionState![id_2]?.status ?? "pending");
    if (statuses.some(s => s === "failure")) return "failure";
    // A child for-each that finalized as `partial` should bubble up so the
    // block group renders amber instead of falling through to "pending".
    if (statuses.some(s_0 => s_0 === "partial")) return "partial";
    if (statuses.some(s_1 => s_1 === "running")) return "running";
    if (statuses.some(s_2 => s_2 === "waiting")) return "waiting";
    if (statuses.every(s_3 => s_3 === "success")) return "success";
    if (statuses.every(s_4 => s_4 === "skipped" || s_4 === "cancelled")) return "skipped";
    return "pending";
  }, [run?.executionState]);
  const normalizeCompletedNodeStatus = useCallback((status_0?: string): "success" | "partial" | "failure" | "running" | "waiting" | "pending" | "skipped" => {
    const normalized = status_0 ?? "pending";
    if ((run?.status === "failure" || run?.status === "partial" || run?.status === "success") && (normalized === "pending" || normalized === "idle")) {
      return "skipped";
    }
    if (normalized === "idle") return "skipped";
    if (normalized === "success" || normalized === "partial" || normalized === "failure" || normalized === "running" || normalized === "waiting" || normalized === "pending" || normalized === "skipped") {
      return normalized;
    }
    return "pending";
  }, [run?.status]);

  // Loading State — skeleton mirrors final layout so content drops in without shift
  if (loading) {
    return <RunDetailSkeleton compact={compact} />;
  }

  // Error State (also guard for missing detail fields from slim list responses)
  if (error || !run || !run.workflowDefinition || !run.executionState) {
    return <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 dark:bg-red-900/20">
          <IconXmarkOutline18 className="h-8 w-8 text-red-600 dark:text-red-500" />
        </div>
        <h3 className="text-ds-text-primary mb-2 text-lg font-semibold">Failed to load run details</h3>
        <p className="body-sm mb-6">{error?.message || "Run not found"}</p>
        <div className="flex gap-3">
          {compact && onClose ? <Button variant="white" onClick={onClose}>
              <span className="text-xs">Close</span>
            </Button> : null}
          <Button onClick={() => window.location.reload()}>
            <span className="text-xs">Try Again</span>
          </Button>
        </div>
      </div>;
  }
  const duration = getDuration(run);
  const nodes_0 = Object.entries(run.workflowDefinition.nodes);
  const executionState_0 = run.executionState;
  const topLevelNodes_0 = nodes_0.filter(([nodeId_4]) => !nodesInsideForEach.has(nodeId_4) && !nodesInBlocks_0.has(nodeId_4));

  // Build unified timeline items: regular nodes + block groups
  type TimelineItem = {
    kind: "node";
    nodeId: string;
    nodeData: (typeof nodes_0)[0][1];
    sortTime: number;
  } | {
    kind: "block";
    blockNodeId: string;
    meta: BlockExpansionMeta;
    sortTime: number;
  };
  const timelineItems: TimelineItem[] = [];

  // Add regular top-level nodes
  for (const [nodeId_5, nodeData_0] of topLevelNodes_0) {
    const nodeState = executionState_0[nodeId_5];
    const sortTime = nodeState?.finishedAt ? new Date(nodeState.finishedAt).getTime() : 0;
    timelineItems.push({
      kind: "node",
      nodeId: nodeId_5,
      nodeData: nodeData_0,
      sortTime
    });
  }

  // Add block groups as single timeline entries
  for (const [blockNodeId_2, {
    meta: meta_3
  }] of blockGroups_0) {
    if (nodesInsideForEach.has(blockNodeId_2)) continue;

    // Sort by earliest finished among expanded nodes
    let earliest = 0;
    for (const expandedId_1 of meta_3.expandedIds) {
      const state_12 = executionState_0[expandedId_1];
      if (state_12?.finishedAt) {
        const t_1 = new Date(state_12.finishedAt).getTime();
        if (earliest === 0 || t_1 < earliest) earliest = t_1;
      }
    }
    timelineItems.push({
      kind: "block",
      blockNodeId: blockNodeId_2,
      meta: meta_3,
      sortTime: earliest
    });
  }

  // Sort timeline by finishedAt
  timelineItems.sort((a_0, b) => {
    if (a_0.sortTime === 0 && b.sortTime === 0) return 0;
    if (a_0.sortTime === 0) return 1;
    if (b.sortTime === 0) return -1;
    return a_0.sortTime - b.sortTime;
  });
  return <div>
      {!hideHeader && <RunTimelineHeader run={run} workflowName={workflowName} workflowHref={workflowHref ?? `/workflows/edit?id=${run.workflowId}`} duration={duration} compact={compact} copiedId={copiedId} onCopyRunId={() => copyToClipboard(run.id, "runId")} onCopyRunJson={handleCopyRunJson} onCopyDefinition={handleCopyDefinition} onClose={onClose} onRerun={allowRunActions ? handleRerun : undefined} onRetry={allowRunActions ? handleRetry : undefined} onCancel={allowRunActions ? handleCancel : undefined} cancelling={allowRunActions ? cancelling : undefined} />}
      {showFundsSummary && <div className={compact && !hideHeader ? "mt-4" : "mb-6"}>
          <RunFundsSummary run={run} isSimulation={run.isSimulation} />
        </div>}
      {/* Timeline */}
      <div className={compact && !hideHeader ? "mt-4" : ""}>
        {timelineItems.map((item, index) => {
        if (item.kind === "block") {
          // Render block group
          const {
            blockNodeId: blockNodeId_3,
            meta: meta_4
          } = item;
          const blockStatus = getBlockGroupStatus(meta_4);
          const isBlockExpanded = expandedBlockGroups.has(blockNodeId_3);
          // Compute total block duration
          let blockStart = Infinity;
          let blockEnd = 0;
          for (const expandedId_2 of meta_4.expandedIds) {
            const state_13 = executionState_0[expandedId_2];
            if (state_13?.startedAt) {
              const t_2 = new Date(state_13.startedAt).getTime();
              if (t_2 < blockStart) blockStart = t_2;
            }
            if (state_13?.finishedAt) {
              const t_3 = new Date(state_13.finishedAt).getTime();
              if (t_3 > blockEnd) blockEnd = t_3;
            }
          }
          const blockDuration = blockStart < Infinity && blockEnd > 0 ? blockEnd - blockStart : undefined;
          return <RunTimelineNode key={`block-${blockNodeId_3}`} nodeId={blockNodeId_3} displayName={`Block: ${meta_4.blockId}`} nodeType="block" status={blockStatus} duration={blockDuration} isFirst={index === 0} isLast={index === timelineItems.length - 1} isExpanded={isBlockExpanded} onToggle={() => toggleBlockGroup(blockNodeId_3)} badge={{
            label: `v${meta_4.blockVersion}`,
            className: "bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-400"
          }}>
                {/* Block child steps */}
                <div>
                  <label className="micro-label">Steps ({meta_4.expandedIds.length})</label>
                  <div className="mt-2">
                    {meta_4.expandedIds.map((expandedId_3, childIdx) => {
                  const childDef = run.workflowDefinition?.nodes[expandedId_3];
                  const childState = executionState_0[expandedId_3];
                  const stepKey = `block-${blockNodeId_3}-${expandedId_3}`;

                  // Resolve wallet address from the child node's own connector
                  const childConnector = childDef?.connector;
                  const childConnectorInstance = childConnector?.id ? connectors.find(c => c.id === childConnector.id) : undefined;
                  const childWalletInstance = !childConnectorInstance && childConnector?.id ? wallets.find(w => w.id === childConnector.id) : undefined;
                  const childWalletAddress = childWalletInstance?.address;
                  const childResolvedVisual = childDef?.type ? resolveNodeVisual(childDef.type, allDefinitions, connectorTypes) : undefined;
                  return <IterationStepRow key={expandedId_3} childNodeId={expandedId_3} childState={childState ? {
                    ...childState,
                    status: normalizeCompletedNodeStatus(childState.status)
                  } : childState} childDef={childDef} childLogoUrl={childResolvedVisual?.logoUrl} childConnector={childResolvedVisual?.connector} iterStepKey={stepKey} isExpanded={expandedIterationSteps.has(stepKey)} isFirst={childIdx === 0} isLast={childIdx === meta_4.expandedIds.length - 1} onToggle={() => {
                    setExpandedIterationSteps(prev_2 => {
                      const next_1 = new Set(prev_2);
                      if (next_1.has(stepKey)) {
                        next_1.delete(stepKey);
                      } else {
                        next_1.add(stepKey);
                      }
                      return next_1;
                    });
                  }} expandedPayloads={expandedPayloads} setExpandedPayloads={setExpandedPayloads} executionState={executionState_0} getNodeDisplayName={getNodeDisplayName} walletAddress={childWalletAddress} />;
                })}
                  </div>
                </div>
              </RunTimelineNode>;
        }

        // Render regular node
        const {
          nodeId: nodeId_6,
          nodeData: nodeData_1
        } = item;
        const nodeState_0 = executionState_0[nodeId_6];
        const isExpanded = expandedNodes.has(nodeId_6);
        const nodeDuration = nodeState_0?.startedAt && nodeState_0?.finishedAt ? new Date(nodeState_0.finishedAt).getTime() - new Date(nodeState_0.startedAt).getTime() : undefined;
        const nodeTypeBadge = getNodeTypeBadge(nodeData_1.type);

        // Build connector prop for this node
        const resolvedVisual = resolveNodeVisual(nodeData_1.type, allDefinitions, connectorTypes);
        const definition_0 = definitionMap.get(nodeData_1.type) ?? resolvedVisual.definition;
        const connectorProp = resolvedVisual.connector;
        const connectorTypeDef = connectorProp ? connectorTypes.find(ct => ct.type === connectorProp.type) : undefined;

        // Resolve connector instance name and wallet address
        const nodeConnector = nodeData_1.connector;
        const connectorInstance = nodeConnector?.id ? connectors.find(c_0 => c_0.id === nodeConnector.id) : undefined;
        const walletInstance = !connectorInstance && nodeConnector?.id ? wallets.find(w_0 => w_0.id === nodeConnector.id) : undefined;
        const connectorName = connectorInstance?.name || walletInstance?.name || connectorTypeDef?.name || nodeConnector?.type;
        const walletAddress = walletInstance?.address;
        const slackPayloadHeading = getSlackPayloadHeading(nodeData_1.type, nodeData_1.payload, nodeState_0?.result, slackChannelsMap);
        return <RunTimelineNode key={nodeId_6} nodeId={nodeId_6} displayName={getNodeDisplayName(nodeData_1.type)} nodeType={nodeData_1.type} status={normalizeCompletedNodeStatus(nodeState_0?.status)} duration={nodeDuration} isFirst={index === 0} isLast={index === timelineItems.length - 1} isExpanded={isExpanded} onToggle={() => toggleNode(nodeId_6)} badge={nodeTypeBadge} logoUrl={resolvedVisual.logoUrl} connector={connectorProp} connectorName={nodeConnector ? connectorName : undefined} walletAddress={nodeConnector ? walletAddress : undefined}>
              {definition_0?.fundsMovement && <FundsMovementBanner movement={definition_0.fundsMovement} payload={nodeData_1.payload} result={nodeState_0?.result} resultSchema={definition_0.resultSchema} payloadSchema={definition_0.payloadSchema} walletAddress={nodeConnector ? walletAddress : undefined} isSimulation={run.isSimulation} executionStatus={nodeState_0?.status} />}
              {/* Payload */}
              {nodeData_1.payload && Object.keys(nodeData_1.payload).length > 0 && (nodeData_1.type === "code-transform" ? <div>
                    <PayloadSectionHeader destination={slackPayloadHeading} />
                    <div className="mt-1">
                      <CodeTransformPayload payload={nodeData_1.payload} expandAll={expandedPayloads.has(nodeId_6)} executionState={executionState_0} />
                    </div>
                  </div> : <div>
                    <div className="flex items-center justify-between">
                      <PayloadSectionHeader destination={slackPayloadHeading} />
                      {!expandedPayloads.has(nodeId_6) && hasNestedContent(nodeData_1.payload) && <button onClick={() => setExpandedPayloads(prev_3 => new Set(prev_3).add(nodeId_6))} className="micro-label text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                          Expand all
                        </button>}
                    </div>
                    <div className="emboss-inner !bg-ds-neutral-100 dark:!bg-ds-neutral-800 mt-1 max-h-[50dvh] overflow-y-auto rounded-lg p-4 font-mono text-xs">
                      <EnhancedJsonViewWithCopy value={nodeData_1.payload} defaultExpanded={true} expandAll={expandedPayloads.has(nodeId_6)} executionState={executionState_0} showExpandAll={false} slackChannelsMap={slackChannelsMap} />
                    </div>
                  </div>)}
              {/* For-each Iterations */}
              {nodeData_1.type === "for-each" && nodeState_0?.iterations && Array.isArray(nodeState_0.iterations) && <div>
                  <label className="micro-label">Iterations ({nodeState_0.iterations.length})</label>
                  <div className="mt-2 space-y-3">
                    {nodeState_0.iterations.map((iteration: Record<string, any>, iterIndex: number) => {
                const itemVarKey = (nodeData_1.payload as any)?.itemVar || "$item";
                const childStates = Object.values(iteration);
                const stateWithLoopVars = childStates.find((s_5: any) => s_5?.context?.loopVars?.[itemVarKey] != null);
                const loopItem = (stateWithLoopVars as any)?.context?.loopVars?.[itemVarKey];
                const itemLabel = loopItem?.name || loopItem?.id || `Item ${iterIndex + 1}`;
                return <div key={iterIndex}>
                          {/* Iteration header */}
                          <div className="label-xs-caps text-ds-text-secondary mb-1.5">
                            #{iterIndex + 1}:{" "}
                            {typeof itemLabel === "object" ? JSON.stringify(itemLabel) : String(itemLabel)}
                          </div>
                          {/* Child steps with curved branch connectors */}
                          <div>
                            {(() => {
                      const entries = getIterationEntriesInDefinitionOrder(run.workflowDefinition!.nodes, nodeId_6, iteration);
                      return entries.map(([childNodeId, childState_0]: [string, any], childIdx_0: number) => {
                        const childDef_0 = run.workflowDefinition?.nodes[childNodeId];
                        const iterStepKey = `${nodeId_6}-iter-${iterIndex}-${childNodeId}`;
                        const childResolvedVisual_0 = childDef_0?.type ? resolveNodeVisual(childDef_0.type, allDefinitions, connectorTypes) : undefined;
                        const childActionDef = childDef_0?.type ? definitionMap.get(childDef_0.type) : undefined;
                        return <IterationStepRow key={childNodeId} childNodeId={childNodeId} childState={childState_0} childDef={childDef_0} childLogoUrl={childResolvedVisual_0?.logoUrl} childConnector={childResolvedVisual_0?.connector} iterStepKey={iterStepKey} isExpanded={expandedIterationSteps.has(iterStepKey)} actionDefinition={childActionDef} isSimulation={run.isSimulation} isFirst={childIdx_0 === 0} isLast={childIdx_0 === entries.length - 1} onToggle={() => {
                          setExpandedIterationSteps(prev_4 => {
                            const next_2 = new Set(prev_4);
                            if (next_2.has(iterStepKey)) {
                              next_2.delete(iterStepKey);
                            } else {
                              next_2.add(iterStepKey);
                            }
                            return next_2;
                          });
                        }} expandedPayloads={expandedPayloads} setExpandedPayloads={setExpandedPayloads} executionState={executionState_0} getNodeDisplayName={getNodeDisplayName} walletAddress={walletAddress} />;
                      });
                    })()}
                          </div>
                        </div>;
              })}
                  </div>
                </div>}
              {/* Auto-swap banner — shown when the interceptor swapped before this action */}
              {nodeState_0?.result?._autoSwap && Array.isArray(nodeState_0.result._autoSwap) && nodeState_0.result._autoSwap.length > 0 && <AutoSwapBanner events={nodeState_0.result._autoSwap as AutoSwapEvent[]} getWalletById={getWalletById} getWalletByAddress={getWalletByAddress} />}
              <DustOverridesBanner raw={nodeState_0?.result?._dustOverrides} />
              {/* Dust test Tenderly fallback note */}
              {nodeState_0?.result?._dustFallback === "tenderly-standalone" && <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 flex items-start gap-2 rounded-lg px-3 py-2.5">
                  <IconCircleInfoOutline18 className="text-ds-text-muted mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  <span className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">
                    Simulated via Tenderly (non-dustable action)
                  </span>
                </div>}
              {/* Execution Result */}
              {nodeState_0?.result && Object.keys(nodeState_0.result).length > 0 && <div>
                  {!(nodeState_0.result.error && typeof nodeState_0.result.error === "string") && <div className="flex items-center justify-between">
                      <label className="micro-label">Result</label>
                      {!expandedPayloads.has(`${nodeId_6}-result`) && hasNestedContent(nodeState_0.result) && <button onClick={() => setExpandedPayloads(prev_5 => new Set(prev_5).add(`${nodeId_6}-result`))} className="micro-label text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                          Expand all
                        </button>}
                    </div>}
                  <div className="mt-1">
                    <ResultDisplay result={nodeState_0.result} expandAll={expandedPayloads.has(`${nodeId_6}-result`)} nodeInfo={{
                name: getNodeDisplayName(nodeData_1.type),
                type: nodeData_1.type
              }} nodeState={{
                input: nodeData_1.payload,
                result: nodeState_0.result
              }} workflowId={run.workflowId} runId={run.id} onFixWithAI={onFixWithAI} onGetSupport={handleGetSupport} slackChannelsMap={slackChannelsMap} />
                  </div>
                </div>}
            </RunTimelineNode>;
      })}
      </div>
    </div>;
}